"use client";

import * as React from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from "framer-motion";
import { IconCamera, IconUser } from "@tabler/icons-react";
import { cn } from "@/lib/utils";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type AvatarStatus = "online" | "offline" | "away" | "busy";
type AvatarImageStatus = "idle" | "loading" | "loaded" | "error";
type AvatarImageLoadingStatus = Exclude<AvatarImageStatus, "idle">;
export type AvatarFallbackTone =
  | "neutral"
  | "blue"
  | "red"
  | "violet"
  | "emerald"
  | "amber"
  | "rose"
  | "sky";

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: AvatarSize;
  status?: AvatarStatus | null;
  /** When true, shows a status ring even without an explicit status value. */
  showStatus?: boolean;
  /** Hover tooltip label anchored above the avatar. */
  tooltip?: React.ReactNode;
  /** Subtle hover affordance on the avatar ring. Defaults to true when `tooltip` is set. */
  interactive?: boolean;
}

export interface AvatarImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "size"> {
  onLoadingStatusChange?: (status: AvatarImageLoadingStatus) => void;
}

export interface AvatarFallbackProps extends React.HTMLAttributes<HTMLSpanElement> {
  delayMs?: number;
  tone?: AvatarFallbackTone;
}

export interface AvatarGroupItemProps {
  name: string;
  children: React.ReactElement<AvatarProps>;
}

export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  max?: number;
  size?: AvatarSize;
  /** Reveal names on hover without moving the avatar stack. */
  interactive?: boolean;
  children: React.ReactNode;
}

export interface AvatarUploadProps
  extends Omit<React.LabelHTMLAttributes<HTMLLabelElement>, "onChange"> {
  size?: AvatarSize;
  src?: string | null;
  alt?: string;
  initials?: string;
  accept?: string;
  disabled?: boolean;
  onChange?: (file: File | null) => void;
  uploadLabel?: string;
}

const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];
const TOOLTIP_SPRING = { type: "spring" as const, stiffness: 420, damping: 32, mass: 0.45 };
const TOOLTIP_EASE: [number, number, number, number] = [0.23, 1, 0.32, 1];
const HOVER_SPRING = { type: "spring" as const, stiffness: 380, damping: 28, mass: 0.42 };

const sizeClasses: Record<AvatarSize, string> = {
  xs: "size-6 text-[10px] [&_[data-slot=avatar-status]]:size-1.5 [&_[data-slot=avatar-status]]:ring-[1.5px]",
  sm: "size-8 text-[11px] [&_[data-slot=avatar-status]]:size-2 [&_[data-slot=avatar-status]]:ring-2",
  md: "size-10 text-xs [&_[data-slot=avatar-status]]:size-2.5 [&_[data-slot=avatar-status]]:ring-2",
  lg: "size-12 text-sm [&_[data-slot=avatar-status]]:size-3 [&_[data-slot=avatar-status]]:ring-2",
  xl: "size-16 text-base [&_[data-slot=avatar-status]]:size-3.5 [&_[data-slot=avatar-status]]:ring-[2.5px]",
};

const iconSizes: Record<AvatarSize, string> = {
  xs: "size-3",
  sm: "size-3.5",
  md: "size-4",
  lg: "size-5",
  xl: "size-6",
};

const groupOverlap: Record<AvatarSize, string> = {
  xs: "-ml-2",
  sm: "-ml-2.5",
  md: "-ml-3",
  lg: "-ml-3.5",
  xl: "-ml-4",
};

const tooltipOffsets: Record<AvatarSize, string> = {
  xs: "bottom-[calc(100%+6px)]",
  sm: "bottom-[calc(100%+7px)]",
  md: "bottom-[calc(100%+8px)]",
  lg: "bottom-[calc(100%+9px)]",
  xl: "bottom-[calc(100%+10px)]",
};

const statusColors: Record<AvatarStatus, string> = {
  online: "bg-emerald-500",
  offline: "bg-[color-mix(in_srgb,var(--muted-foreground)_55%,transparent)]",
  away: "bg-amber-400",
  busy: "bg-red-500",
};

const fallbackTones: Record<AvatarFallbackTone, string> = {
  neutral:
    "bg-[color-mix(in_srgb,var(--foreground)_12%,var(--background))] text-[color-mix(in_srgb,var(--foreground)_82%,var(--background))]",
  blue: "bg-sky-700 text-sky-50",
  red: "bg-red-700 text-red-50",
  violet: "bg-violet-700 text-violet-50",
  emerald: "bg-emerald-700 text-emerald-50",
  amber: "bg-amber-700 text-amber-50",
  rose: "bg-rose-700 text-rose-50",
  sky: "bg-sky-600 text-white",
};

const AUTO_TONES: AvatarFallbackTone[] = [
  "blue",
  "red",
  "violet",
  "emerald",
  "amber",
  "rose",
  "sky",
];

const AVATAR_IMAGE_DISPLAY_NAME = "AvatarImage";

const AvatarContext = React.createContext<{
  size: AvatarSize;
  hasImage: boolean;
  imageStatus: AvatarImageStatus;
  setImageStatus: React.Dispatch<React.SetStateAction<AvatarImageStatus>>;
}>({
  size: "md",
  hasImage: false,
  imageStatus: "idle",
  setImageStatus: () => {},
});

function useAvatarContext() {
  return React.useContext(AvatarContext);
}

function getInitials(value: string) {
  const parts = value.trim().split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function toneFromSeed(seed: string): AvatarFallbackTone {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash + seed.charCodeAt(index) * (index + 1)) % AUTO_TONES.length;
  }
  return AUTO_TONES[hash] ?? "blue";
}

function isAvatarImageElement(child: React.ReactNode) {
  if (!React.isValidElement(child)) return false;
  const type = child.type as { displayName?: string };
  return type.displayName === AVATAR_IMAGE_DISPLAY_NAME;
}

function AvatarTooltip({
  label,
  size,
  visible,
  reduceMotion,
}: {
  label: React.ReactNode;
  size: AvatarSize;
  visible: boolean;
  reduceMotion: boolean;
}) {
  return (
    <AnimatePresence>
      {visible ? (
        <motion.span
          role="tooltip"
          data-slot="avatar-tooltip"
          initial={reduceMotion ? false : { opacity: 0, y: 4, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={
            reduceMotion
              ? undefined
              : {
                  opacity: 0,
                  y: 3,
                  scale: 0.95,
                  transition: { duration: 0.1, ease: TOOLTIP_EASE },
                }
          }
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: 0.15, ease: TOOLTIP_EASE }
          }
          className={cn(
            "pointer-events-none absolute left-1/2 z-[130] w-max max-w-[min(16rem,calc(100vw-2rem))] -translate-x-1/2",
            tooltipOffsets[size],
          )}
          style={{ transformOrigin: "bottom center" }}
        >
          <span
            className={cn(
              "relative inline-flex w-full items-center rounded-xl border border-[#ded3c5] bg-[#f7f2e9] px-2.5 py-1.5",
              "break-words text-left",
              "text-[12px] font-medium leading-[1.45] tracking-[-0.01em] text-[#181716]",
              "[box-shadow:0_20px_52px_-28px_rgba(0,0,0,.78),0_8px_24px_-16px_rgba(18,16,13,.42),0_1px_2px_rgba(18,16,13,.10)]",
            )}
          >
            {label}
          </span>
        </motion.span>
      ) : null}
    </AnimatePresence>
  );
}

function AvatarStatusDot({
  status,
}: {
  status: AvatarStatus;
}) {
  return (
    <span
      data-slot="avatar-status"
      aria-hidden="true"
      className={cn(
        "absolute bottom-0 right-0 z-10 rounded-full ring-[var(--background)]",
        statusColors[status],
      )}
    >
      {status === "online" ? (
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-emerald-500/35 motion-safe:animate-[wensity-avatar-online-pulse_2.4s_ease-in-out_infinite]"
        />
      ) : null}
    </span>
  );
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  (
    {
      className,
      size = "md",
      status = null,
      showStatus = false,
      tooltip,
      interactive,
      children,
      onFocus,
      onBlur,
      ...rest
    },
    ref,
  ) => {
    const reduceMotion = useReducedMotion() ?? false;
    const [revealed, setRevealed] = React.useState(false);
    const hasImage = React.Children.toArray(children).some(isAvatarImageElement);
    const [imageStatus, setImageStatus] = React.useState<AvatarImageStatus>(() =>
      hasImage ? "loading" : "idle",
    );
    const resolvedStatus = showStatus && !status ? "online" : status;
    const isInteractive = interactive ?? Boolean(tooltip);
    const contextValue = React.useMemo(
      () => ({
        size,
        hasImage,
        imageStatus,
        setImageStatus,
      }),
      [hasImage, imageStatus, size],
    );

    React.useEffect(() => {
      setImageStatus((current) => {
        if (!hasImage) return "idle";
        return current === "idle" ? "loading" : current;
      });
    }, [hasImage]);

    return (
      <AvatarContext.Provider value={contextValue}>
        <span
          ref={ref}
          data-slot="avatar-root"
          className={cn(
            "group/avatar-root relative inline-flex align-middle",
            isInteractive && "cursor-default",
            className,
          )}
          tabIndex={tooltip ? 0 : undefined}
          onMouseEnter={() => setRevealed(true)}
          onMouseLeave={() => setRevealed(false)}
          onFocus={(event) => {
            setRevealed(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setRevealed(false);
            onBlur?.(event);
          }}
          {...rest}
        >
          <span
            data-slot="avatar"
            data-size={size}
            className={cn("relative inline-flex shrink-0 align-middle", sizeClasses[size])}
          >
            <span
              className={cn(
                "relative flex size-full overflow-hidden rounded-full",
                "bg-[color-mix(in_srgb,var(--foreground)_6%,var(--background))]",
                "ring-1 ring-inset ring-[color-mix(in_srgb,var(--foreground)_12%,transparent)]",
              )}
            >
              {children}
            </span>
            {resolvedStatus ? (
              <AvatarStatusDot status={resolvedStatus} />
            ) : null}
          </span>
          {tooltip ? (
            <AvatarTooltip
              label={tooltip}
              size={size}
              visible={revealed}
              reduceMotion={reduceMotion}
            />
          ) : null}
        </span>
      </AvatarContext.Provider>
    );
  },
);
Avatar.displayName = "Avatar";

export const AvatarImage = React.forwardRef<HTMLImageElement, AvatarImageProps>(
  ({ className, alt = "", onLoadingStatusChange, onLoad, onError, src }, ref) => {
    const { setImageStatus } = useAvatarContext();
    const reduceMotion = useReducedMotion() ?? false;
    const [status, setStatus] = React.useState<AvatarImageLoadingStatus>(
      () => (src ? "loading" : "error"),
    );
    const updateStatus = React.useCallback(
      (nextStatus: AvatarImageLoadingStatus) => {
        setStatus(nextStatus);
        setImageStatus(nextStatus);
      },
      [setImageStatus],
    );

    React.useEffect(() => {
      updateStatus(src ? "loading" : "error");
    }, [src, updateStatus]);

    React.useEffect(() => {
      onLoadingStatusChange?.(status);
    }, [onLoadingStatusChange, status]);

    if (status === "error") return null;

    return (
      <motion.img
        ref={ref}
        src={src}
        alt={alt}
        data-slot="avatar-image"
        data-status={status}
        className={cn("absolute inset-0 size-full object-cover", className)}
        initial={false}
        animate={{
          opacity: status === "loading" ? 0 : 1,
          scale: status === "loading" && !reduceMotion ? 0.96 : 1,
        }}
        transition={
          reduceMotion
            ? { duration: 0 }
            : {
                opacity: { duration: 0.28, ease: EASE_OUT },
                scale: HOVER_SPRING,
              }
        }
        onLoad={(event) => {
          updateStatus("loaded");
          onLoad?.(event);
        }}
        onError={(event) => {
          updateStatus("error");
          onError?.(event);
        }}
      />
    );
  },
);
AvatarImage.displayName = AVATAR_IMAGE_DISPLAY_NAME;

export const AvatarFallback = React.forwardRef<HTMLSpanElement, AvatarFallbackProps>(
  ({ className, delayMs = 0, tone, children }, ref) => {
    const { size, hasImage, imageStatus } = useAvatarContext();
    const reduceMotion = useReducedMotion() ?? false;
    const [visible, setVisible] = React.useState(delayMs === 0);
    const shouldShowFallback =
      !hasImage || imageStatus === "loading" || imageStatus === "error";

    React.useEffect(() => {
      if (!shouldShowFallback) {
        setVisible(false);
        return;
      }
      if (delayMs === 0) {
        setVisible(true);
        return;
      }
      setVisible(false);
      const timeout = window.setTimeout(() => setVisible(true), delayMs);
      return () => window.clearTimeout(timeout);
    }, [delayMs, shouldShowFallback]);

    const seed = typeof children === "string" ? children : "";
    const resolvedTone = tone ?? (seed ? toneFromSeed(seed) : "neutral");
    const content = seed ? getInitials(seed) : children;

    return (
      <AnimatePresence>
        {visible && shouldShowFallback ? (
          <motion.span
            ref={ref}
            data-slot="avatar-fallback"
            data-tone={resolvedTone}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0, scale: 0.96 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.22, ease: EASE_OUT }}
            className={cn(
              "absolute inset-0 flex size-full items-center justify-center font-semibold tracking-[-0.02em]",
              fallbackTones[resolvedTone],
              className,
            )}
          >
            {content ?? <IconUser className={iconSizes[size]} stroke={1.75} aria-hidden />}
          </motion.span>
        ) : null}
      </AnimatePresence>
    );
  },
);
AvatarFallback.displayName = "AvatarFallback";

export function AvatarGroupItem({ children }: AvatarGroupItemProps) {
  return children;
}
AvatarGroupItem.displayName = "AvatarGroupItem";

function isAvatarGroupItem(
  child: React.ReactNode,
): child is React.ReactElement<AvatarGroupItemProps> {
  if (!React.isValidElement(child)) return false;
  const type = child.type as { displayName?: string };
  return type.displayName === "AvatarGroupItem";
}

function resolveGroupAvatar(child: React.ReactNode, interactive: boolean) {
  if (isAvatarGroupItem(child)) {
    const avatar = child.props.children;
    if (!interactive) {
      return React.cloneElement(avatar, {
        tooltip: undefined,
        interactive: false,
      });
    }
    return React.cloneElement(avatar, {
      tooltip: avatar.props.tooltip ?? child.props.name,
      interactive: avatar.props.interactive ?? true,
    });
  }
  return child;
}

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, max = 4, size = "md", interactive = true, children, ...props }, ref) => {
    const childArray = React.Children.toArray(children);
    const avatars = childArray.map((child) => resolveGroupAvatar(child, interactive));
    const visible = avatars.slice(0, max);
    const overflow = avatars.length - max;

    return (
      <div
        ref={ref}
        data-slot="avatar-group"
        className={cn(
          "inline-flex items-center",
          interactive && "group/avatar-group",
          className,
        )}
        {...props}
      >
        {visible.map((child, index) => (
          <div
            key={index}
            data-slot="avatar-group-item"
            className={cn("relative", index > 0 && groupOverlap[size])}
            style={{ zIndex: visible.length - index }}
          >
            {React.isValidElement<{ size?: AvatarSize }>(child)
              ? React.cloneElement(child, { size: child.props.size ?? size })
              : child}
          </div>
        ))}
        {overflow > 0 ? (
          <div className={cn("relative", groupOverlap[size])}>
            <Avatar
              size={size}
              tooltip={interactive ? `+${overflow} more` : undefined}
              interactive={interactive}
            >
              <AvatarFallback tone="neutral" className="text-[11px] font-medium">
                +{overflow}
              </AvatarFallback>
            </Avatar>
          </div>
        ) : null}
      </div>
    );
  },
);
AvatarGroup.displayName = "AvatarGroup";

export const AvatarUpload = React.forwardRef<HTMLLabelElement, AvatarUploadProps>(
  (
    {
      className,
      size = "lg",
      src,
      alt = "Profile photo",
      initials,
      accept = "image/*",
      disabled = false,
      onChange,
      uploadLabel = "Upload profile photo",
      children,
      ...props
    },
    ref,
  ) => {
    const reduceMotion = useReducedMotion() ?? false;
    const inputRef = React.useRef<HTMLInputElement>(null);
    const localPreviewRef = React.useRef<string | null>(null);
    const [preview, setPreview] = React.useState<string | null>(src ?? null);
    const [hovered, setHovered] = React.useState(false);
    const revokeLocalPreview = React.useCallback(() => {
      if (!localPreviewRef.current) return;
      URL.revokeObjectURL(localPreviewRef.current);
      localPreviewRef.current = null;
    }, []);

    React.useEffect(() => {
      revokeLocalPreview();
      setPreview(src ?? null);
    }, [revokeLocalPreview, src]);

    React.useEffect(() => revokeLocalPreview, [revokeLocalPreview]);

    return (
      <label
        ref={ref}
        data-slot="avatar-upload"
        aria-label={uploadLabel}
        className={cn(
          "group/avatar-upload relative inline-flex cursor-pointer align-middle",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        {...props}
      >
        <Avatar size={size} interactive={false}>
          {preview ? (
            <AvatarImage src={preview} alt={alt} />
          ) : (
            <AvatarFallback delayMs={0} tone={initials ? toneFromSeed(initials) : "neutral"}>
              {initials ?? <IconUser stroke={1.75} />}
            </AvatarFallback>
          )}
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center rounded-full text-white"
            initial={false}
            animate={{
              backgroundColor: hovered ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0)",
            }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.2, ease: EASE_OUT }}
          >
            <motion.span
              animate={{
                opacity: hovered ? 1 : 0,
                y: hovered ? 0 : 4,
                scale: hovered ? 1 : 0.92,
              }}
              transition={reduceMotion ? { duration: 0 } : TOOLTIP_SPRING}
            >
              <IconCamera className={iconSizes[size]} stroke={1.75} />
            </motion.span>
          </motion.span>
        </Avatar>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={disabled}
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            if (file) {
              const objectUrl = URL.createObjectURL(file);
              revokeLocalPreview();
              localPreviewRef.current = objectUrl;
              setPreview(objectUrl);
            } else {
              revokeLocalPreview();
              setPreview(src ?? null);
            }
            onChange?.(file);
          }}
        />
        {children}
      </label>
    );
  },
);
AvatarUpload.displayName = "AvatarUpload";

const AVATAR_STYLE_ID = "wensity-avatar-styles-v2";

function ensureAvatarStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(AVATAR_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = AVATAR_STYLE_ID;
  style.textContent = `
    @keyframes wensity-avatar-online-pulse {
      0%, 100% { transform: scale(1); opacity: 0.35; }
      50% { transform: scale(1.85); opacity: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      [data-slot="avatar-status"] .motion-safe\\:animate-\\[wensity-avatar-online-pulse_2\\.4s_ease-in-out_infinite\\] {
        animation: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}

if (typeof document !== "undefined") {
  ensureAvatarStyles();
}
